import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';

const getAuthConfig = () => {
  if (!hasAuthSession()) return null;
  return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

const BlockFReport = () => {
  const navigate = useNavigate();

  // State for report data
  const [itemData, setItemData] = useState({});
  const [blockFData, setBlockFData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';

  // Memoized data transformation
  const transformBlockFData = useCallback((data) => {
    const initialData = {};
    data.forEach((item, index) => {
      initialData[item.itemCode] = {
        dslNo: item.dslNo,
        surveyYear: item.surveyYear,
        itemCode: item.itemCode,
        expenditure: item.expenditure,
        statusFlag: item.statusFlag,
        ItemsName: item.ItemsName,
        expenditureClass: 'textBox_back_color_white',
        index: index // Add index for reference
      };
    });
    return initialData;
  }, []);

  // Fetch data function
  const fetchBlockFData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };

    showLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockFForm`, requestPayload, config);
      const { data, status } = response.data;

      if (status && data?.blockFData?.length > 0) {
        setBlockFData(data.blockFData);
        const transformedData = transformBlockFData(data.blockFData);
        setItemData(transformedData);
        console.log('Block F data loaded successfully:', data.blockFData.length, 'items');
        console.log('Block F data structure:', data.blockFData);
        console.log('Transformed data:', transformedData);

        // Log each item with its index and details
        data.blockFData.forEach((item, index) => {
          console.log(`Item ${index}:`, {
            itemCode: item.itemCode,
            ItemsName: item.ItemsName,
            expenditure: item.expenditure,
            index: index
          });
        });

        // Add debug info to see what we're getting
        console.log('=== DEBUGGING BLOCK F DATA ===');
        console.log('Total items:', data.blockFData.length);
        console.log('Items with "Repair" in name:', data.blockFData.filter(item => item.ItemsName && item.ItemsName.toLowerCase().includes('repair')));
        console.log('Items with "Maintenance" in name:', data.blockFData.filter(item => item.ItemsName && item.ItemsName.toLowerCase().includes('maintenance')));
        console.log('All item names:', data.blockFData.map(item => item.ItemsName));
      } else {
        setBlockFData([]);
        toast.warning('No Block F data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockFData([]);
    } finally {
      showLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, transformBlockFData]);

  useEffect(() => {
    fetchBlockFData();
  }, [fetchBlockFData]);

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    const isHindi = storedLanguage === 'hi';
    return {
      slNo: isHindi ? asiTitle.hi.SL_NO : asiTitle.en.SL_NO,
      items: isHindi ? asiTitle.hi.ITEMS : asiTitle.en.ITEMS,
      expenditure: isHindi ? asiTitle.hi.EXPENDITURE : asiTitle.en.EXPENDITURE,
      dslNo: isHindi ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO,
      surveyYear: isHindi ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR,
      blockFTitle: isHindi ? asiTitle.hi.BLOCK_F_TITLE : asiTitle.en.BLOCK_F_TITLE
    };
  }, [storedLanguage]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 12px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 11px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .item-name {
            text-align: left;
            font-weight: 500;
            padding-left: 8px;
          }
          .report-table .bold-text {
            font-weight: bold;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
       
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <div className="report-title">
            <div dangerouslySetInnerHTML={{ __html: tableHeaders.blockFTitle }} />
          </div>

          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>


          {blockFData.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>{tableHeaders.slNo}</th>
                    <th style={{ width: '60%' }}>{tableHeaders.items}</th>
                    <th style={{ width: '30%' }}>{tableHeaders.expenditure}</th>
                  </tr>
                  <tr>
                    <th>1</th>
                    <th>2</th>
                    <th>3</th>
                  </tr>
                </thead>
                <tbody>
                  {blockFData.map((item, index) => {
                    // Skip certain rows as per BlockF.jsx logic
                    if (![0, 1, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].includes(index)) {
                      return null;
                    }

                    const srNo = [0, 1, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].indexOf(index) + 1;
                    const isBold = index === 7; // Only row 7 is bold in BlockF

                    // Special handling for row 2 (index 1) - it has sub-items
                    if (index === 1) {
                      // Find the correct data for sub-items
                      const subItem1 = blockFData.find(item =>
                        item.ItemsName && item.ItemsName.toLowerCase().includes('building') &&
                        item.ItemsName.toLowerCase().includes('construction')
                      );
                      const subItem2 = blockFData.find(item =>
                        item.ItemsName && item.ItemsName.toLowerCase().includes('other fixed assets')
                      );

                      console.log('Sub-item 1 (Building):', subItem1);
                      console.log('Sub-item 2 (Other Fixed Assets):', subItem2);

                      return (
                        <React.Fragment key={index}>
                          {/* Main row 2 */}
                          <tr>
                            <td>2</td>
                            <td className="item-name">
                              <strong>Repair and Maintenance</strong>
                            </td>
                            <td></td>
                          </tr>
                          {/* Sub-item 2(i) */}
                          <tr>
                            <td></td>
                            <td className="item-name" style={{ paddingLeft: '20px' }}>
                              (i) Repair and Maintenance of Building and other construction
                            </td>
                            <td>
                              {subItem1 ? (itemData[subItem1.itemCode]?.expenditure === "0" ? "" : itemData[subItem1.itemCode]?.expenditure || '') : '0.00'}
                            </td>
                          </tr>
                          {/* Sub-item 2(ii) */}
                          <tr>
                            <td></td>
                            <td className="item-name" style={{ paddingLeft: '20px' }}>
                              (ii) Repair and Maintenance of Other Fixed Assets
                            </td>
                            <td>
                              {subItem2 ? (itemData[subItem2.itemCode]?.expenditure === "0" ? "" : itemData[subItem2.itemCode]?.expenditure || '') : '0.00'}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    }

                    return (
                      <tr key={index}>
                        <td>{![2].includes(index) && srNo}</td>
                        <td className={`item-name ${isBold ? "bold-text" : ""}`}>
                          {item.ItemsName}
                        </td>
                        <td>{itemData[item.itemCode]?.expenditure === "0" ? "" : itemData[item.itemCode]?.expenditure || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No Block F data available for this DSL No.</div>
          )}
        </div>

        {/* Action Buttons */}

        {/* <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/reports')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div> */}

        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockGreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>


      </div>
    </>
  );
};

export default BlockFReport;